'use client';

import { DocumentNode, useMutation } from '@apollo/client';
import {
  ActionIcon,
  Avatar,
  Button,
  CopyButton,
  Group,
  Menu,
  Modal,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import Delete from '@material-design-icons/svg/filled/delete.svg';
import EditIcon from '@material-design-icons/svg/filled/edit.svg';
import MoreHoriz from '@material-design-icons/svg/filled/more_horiz.svg';
import Publish from '@material-design-icons/svg/filled/publish.svg';
import ShareIcon from '@material-design-icons/svg/filled/share.svg';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import {
  IssuesPage_DeleteIssueDocument,
  IssuesPage_GetIssueDocument,
  IssuesPage_MakeIssueActiveDocument,
  IssuesPage_MakeIssueInactiveDocument,
  ProfileContent_EntityFragment,
  ProfileContent_IssuesFragment as Issue,
} from '@/graphql';
import { logger } from '@/utils';

import styles from './IssueCard.module.css';

interface IssueCardProps {
  issue: Issue;
  isInLeaderTab?: boolean;
  currentEntity: ProfileContent_EntityFragment;
  isOwner?: boolean;
  queryForEntity: DocumentNode;
}

export const IssueCard = ({
  issue,
  currentEntity,
  isInLeaderTab = true,
  isOwner = false,
  queryForEntity,
}: IssueCardProps) => {
  const router = useRouter();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const [makeIssueActive, { loading: publishing }] = useMutation(
    IssuesPage_MakeIssueActiveDocument,
    {
      refetchQueries: [
        {
          query: queryForEntity,
          variables: { entityId: currentEntity.id },
        },
        {
          query: IssuesPage_GetIssueDocument,
          variables: { id: issue.id },
        },
      ],
    },
  );
  const [makeIssueInactive, { loading: unpublishing }] = useMutation(
    IssuesPage_MakeIssueInactiveDocument,
    {
      refetchQueries: [
        {
          query: queryForEntity,
          variables: { entityId: currentEntity.id },
        },
        {
          query: IssuesPage_GetIssueDocument,
          variables: { id: issue.id },
        },
      ],
    },
  );
  const [deleteIssue, { loading: deleting }] = useMutation(
    IssuesPage_DeleteIssueDocument,
    {
      refetchQueries: [
        {
          query: queryForEntity,
          variables: { entityId: currentEntity.id },
        },
      ],
    },
  );

  const leaderIsViewingOwnIssue = isInLeaderTab && isOwner;
  const leaderName = leaderIsViewingOwnIssue
    ? 'you'
    : issue.leader.displayNameShort;
  const leaderEntity = issue.leader ? issue.leader : currentEntity;
  const issueUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/profiles/${issue.leader.id}/topics/${issue.id}`
      : `/profiles/${issue.leader.id}/topics/${issue.id}`;

  const handlePublishIssue = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      if (issue.isActive) {
        await makeIssueInactive({ variables: { id: issue.id } });
      } else {
        await makeIssueActive({ variables: { id: issue.id } });
      }
      router.refresh?.();
    } catch (err) {
      logger.error(`Failed to activate issue ${err}`);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteIssue({ variables: { id: issue.id } });
      router.refresh?.();
    } catch (err) {
      logger.error(`Failed to delete issue ${err}`);
    }
    setShowDeleteConfirmation(false);
  };

  return (
    <>
      <Paper
        component={Link}
        href={`/profiles/${issue.leader.id}/topics/${issue.id}`}
        variant="issue"
        className={`${!issue.isActive ? styles.dimmed : ''}`}
        key={issue.id}
        style={{ position: 'relative' }}
      >
        <Group align="center">
          <Avatar
            src={
              isInLeaderTab
                ? issue.issueMediaRels?.[0]?.medium?.link ||
                  leaderEntity?.profilePhoto?.link
                : leaderEntity?.profilePhoto?.link
            }
            variant="profile"
            alt={leaderEntity?.displayNameShort || 'Profile'}
            className={styles.avatar}
          />
          <Stack
            gap="xs"
            style={{ flex: 1 }}
          >
            <Stack className={styles.topicContainer}>
              <Title
                order={3}
                className={styles.topic}
              >
                {issue.topic}
              </Title>
            </Stack>
            <Text
              size="md"
              c="neutral.9"
            >
              {isInLeaderTab
                ? `${issue.supporterCount || '0'} ${issue.supporterCount === 1 ? 'person is' : 'people are'} supporting ${leaderName}`
                : (() => {
                    const otherSupporters = Math.max(
                      (issue.supporterCount || 0) - 1,
                      0,
                    );
                    return otherSupporters === 0
                      ? `You are supporting ${leaderName}`
                      : `You and ${otherSupporters} ${otherSupporters === 1 ? 'other person are' : 'people are'} supporting ${leaderName}`;
                  })()}
            </Text>
          </Stack>
        </Group>

        <div className={styles.menuContainer}>
          <Menu
            position="bottom-end"
            withinPortal
          >
            <Menu.Target>
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={(e) => e.preventDefault()}
              >
                <MoreHoriz />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <CopyButton value={issueUrl}>
                {({ copied, copy }) => (
                  <Menu.Item
                    leftSection={<ShareIcon />}
                    onClick={(e) => {
                      e.preventDefault();
                      copy();
                    }}
                  >
                    {copied ? 'Link Copied!' : 'Copy Link'}
                  </Menu.Item>
                )}
              </CopyButton>
              {leaderIsViewingOwnIssue && (
                <>
                  <Menu.Item
                    leftSection={<EditIcon />}
                    onClick={(e) => {
                      e.preventDefault();
                      router.push(
                        `/profiles/${issue.leader.id}/topics/${issue.id}?mode=edit`,
                      );
                    }}
                  >
                    Edit
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<Publish />}
                    onClick={handlePublishIssue}
                    disabled={publishing || unpublishing}
                  >
                    {issue.isActive ? 'Unpublish' : 'Publish'}
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<Delete />}
                    onClick={(e) => {
                      e.preventDefault();
                      setShowDeleteConfirmation(true);
                    }}
                    disabled={deleting}
                  >
                    Delete Page
                  </Menu.Item>
                </>
              )}
            </Menu.Dropdown>
          </Menu>
        </div>
      </Paper>

      <Modal
        opened={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        title="Delete Page Warning"
      >
        <Text>
          This action is irreversible. If you would like to hide your page from
          public view, you can unpublish the issue instead.
          <br />
          <br />
          Do you want to delete this issue?
        </Text>
        <Group
          justify="flex-end"
          mt="md"
        >
          <Button
            variant="default"
            onClick={() => setShowDeleteConfirmation(false)}
          >
            Keep
          </Button>
          <Button
            color="red"
            onClick={handleDelete}
          >
            Delete
          </Button>
        </Group>
      </Modal>
    </>
  );
};
